import { Button, Card, Form, Input, Radio, Select, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Grid as ZhGrid,
    InputNumber as ZhInputNumber,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";
import { confirmDialog } from "@/utils/confirmDialog";

export interface AdvertisementFormContent {
    fileId?: number;
    advertId?: number;
    linkType?: number;
    link?: string;
    activityCode?: string;
    sort?: number;
    [key: string]: unknown;
}

export interface AdvertisementFormValues {
    positionType?: number;
    position?: number;
    displayForm: number;
    timeInterval?: number;
    beginTime?: string;
    endTime?: string;
    content: AdvertisementFormContent[];
}

export type AdvertisementFormPayload = Omit<AdvertisementFormValues, "content"> & {
    content?: AdvertisementFormContent[];
};

export interface AdvertisementFormPageProps {
    id?: number;
    loadDetail?: (id: number) => Promise<AdvertisementFormValues & { id?: number }>;
    update?: (id: number, values: AdvertisementFormPayload) => Promise<unknown>;
    loadAdvertOptions?: (keyword: string) => Promise<AdvertisementSelectorOption[]>;
    loadCampaignOptions?: (keyword: string) => Promise<AdvertisementSelectorOption[]>;
}

export interface AdvertisementSelectorOption {
    label: string;
    value: string | number;
}

interface AdvertisementUploadedFile {
    id: number;
    path?: string;
}

/** 复用 Vue PC 广告位详情接口，保持编辑页的原始数据来源。 */
export async function loadAdvertisementDetail(id: number): Promise<AdvertisementFormValues & { id?: number }> {
    const response = await request.service.get<AdvertisementFormValues & { id?: number }>(
        `/dataService/admin/advertisement-position/${id}`
    );
    return response.data;
}

/** 复用 Vue PC 广告位更新接口，并将上传后的图片 ID 与访问地址回填到保存 payload。 */
export async function updateAdvertisement(id: number, values: AdvertisementFormPayload): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/advertisement-position/${id}`, values);
    return response.data;
}

/** 复用 Vue adImg 的上传契约，使用 FormData.file 获取文件 ID 和访问路径。 */
export async function uploadAdvertisementImage(file: Blob): Promise<AdvertisementUploadedFile> {
    const formData = new FormData();
    formData.append("file", file);
    const uploadedFile = await fileService.uploadFile(formData);
    return { id: Number(uploadedFile.id), path: uploadedFile.path };
}

/** 复用 Vue imgContent 的 PC 广告页远程选择接口。 */
export async function loadAdvertisementAdvertOptions(keyword: string): Promise<AdvertisementSelectorOption[]> {
    const response = await request.service.get<{
        list?: Array<{ advert?: { id?: number | string; title?: string } }>;
    }>("/dataService/admin/advert/paging", { params: { key: keyword, classify: 1, page: 1, size: 50 } });
    return (response.data.list || [])
        .filter(item => item.advert?.id != null)
        .map(item => ({ label: `${item.advert?.title || ""} | ${item.advert?.id}`, value: item.advert!.id! }));
}

/** 复用 Vue imgContent 的活动远程选择接口。 */
export async function loadAdvertisementCampaignOptions(keyword: string): Promise<AdvertisementSelectorOption[]> {
    const response = await request.service.get<Array<{ code?: string | number; title?: string }>>(
        "/admin/campaign/selector",
        { params: { keyword } }
    );
    return (response.data || [])
        .filter(item => item.code != null)
        .map(item => ({ label: item.title || `-- | ${item.code}`, value: item.code! }));
}

const initialValues: AdvertisementFormValues = {
    displayForm: 1,
    timeInterval: 1,
    beginTime: "",
    endTime: "",
    content: [{}]
};

function dateOnly(value?: string): string {
    return value?.slice(0, 10) || "";
}

/** 将日期控件值转换成 Vue 接口约定的起止时间。 */
function buildDateTime(value: string | undefined, endOfDay: boolean): string | undefined {
    const date = dateOnly(value);
    if (!date) return undefined;
    // 后端字段是 LocalDateTime；旧 Vue 也通过 LOG_DATE_FORMAT_* 提交 ISO 本地时间。
    return `${date}T${endOfDay ? "23:59:59" : "00:00:00"}`;
}

/** 对齐 Vue ad/edit.vue：特定广告位不支持多图轮播。 */
export function isAdvertisementCarouselDisabled(positionType?: number, position?: number): boolean {
    if (positionType === 6) return [6, 7, 8].includes(position ?? 0);
    return position === 2;
}

/** 按 Vue imgContent 校验规则检查图片、跳转值和轮播排序。 */
function validateContent(values: AdvertisementFormValues): string[] {
    const errors: string[] = [];
    if (values.displayForm === 3) return errors;
    values.content.forEach(item => {
        if (!item.fileId) errors.push("请上传广告图片");
        if (item.linkType === 1 && !item.advertId) errors.push("请选择广告页");
        if (item.linkType === 2 && !item.link?.trim()) errors.push("请输入链接");
        if (item.linkType === 3 && !item.activityCode?.trim()) errors.push("请选择活动");
        if (
            values.displayForm === 2 &&
            (!Number.isInteger(item.sort) || Number(item.sort) < 1 || Number(item.sort) > 5)
        ) {
            errors.push("请填写权重排序");
        }
    });
    if (values.displayForm === 2) {
        const sorts = values.content.map(item => item.sort).filter((sort): sort is number => sort != null);
        if (new Set(sorts).size !== sorts.length) errors.push("填写的权重排序不支持重复，请修改");
    }
    return errors;
}

/** PC 广告位编辑基础表单，保留 Vue 的展示形式、周期和内容数组提交结构。 */
export function AdvertisementFormPage({
    id: propId,
    loadDetail = loadAdvertisementDetail,
    update = updateAdvertisement,
    loadAdvertOptions = loadAdvertisementAdvertOptions,
    loadCampaignOptions = loadAdvertisementCampaignOptions
}: AdvertisementFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = propId ?? Number(searchParams.get("id"));
    const [values, setValues] = useState<AdvertisementFormValues>(initialValues);
    const [loading, setLoading] = useState(id > 0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // 广告周期沿用 Vue el-form 的字段级校验，避免提交后在页面顶部弹出错误。
    const [periodError, setPeriodError] = useState<string | undefined>();
    const [uploadedImagePaths, setUploadedImagePaths] = useState<Record<number, string>>({});
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
    const [advertOptions, setAdvertOptions] = useState<Record<number, AdvertisementSelectorOption[]>>({});
    const [campaignOptions, setCampaignOptions] = useState<Record<number, AdvertisementSelectorOption[]>>({});
    const carouselDisabled = isAdvertisementCarouselDisabled(values.positionType, values.position);

    useEffect(() => {
        if (!(id > 0)) {
            setLoading(false);
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadDetail(id)
            .then(detail => {
                if (disposed) return;
                setValues({
                    ...detail,
                    displayForm: Number(detail.displayForm) || 1,
                    timeInterval: Number(detail.timeInterval) || 1,
                    beginTime: dateOnly(detail.beginTime),
                    endTime: dateOnly(detail.endTime),
                    // Vue ad/edit.vue 在空接口返回时仍保留一个可上传内容槽位。
                    content: detail.content?.length ? detail.content : [{}]
                });
                setLoading(false);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("广告位详情加载失败");
                setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    /** 编辑态回显 Vue adImg 已保存的图片地址，避免只显示“替换图片”占位。 */
    useEffect(() => {
        let disposed = false;
        const entries = values.content
            .map((item, index) => [index, item.fileId] as const)
            .filter((entry): entry is readonly [number, number] => Number.isFinite(entry[1]));
        void Promise.all(
            entries.map(async ([index, fileId]) => {
                try {
                    const file = await fileService.getFileById(fileId);
                    return [index, file.path || ""] as const;
                } catch {
                    return [index, ""] as const;
                }
            })
        ).then(results => {
            if (!disposed) {
                setUploadedImagePaths(current => ({
                    ...current,
                    ...Object.fromEntries(results.filter(([, path]) => path))
                }));
            }
        });
        return () => {
            disposed = true;
        };
    }, [values.content]);

    /** 编辑态回显 Vue 广告页下拉选项文本（例如“213 | 130”），避免只显示编号。 */
    useEffect(() => {
        let disposed = false;
        const entries = values.content
            .map((item, index) => [index, item.advertId] as const)
            .filter((entry): entry is readonly [number, number] => Number.isFinite(entry[1]));
        void Promise.all(
            entries.map(async ([index, advertId]) => {
                try {
                    const options = await loadAdvertOptions(String(advertId));
                    return [index, options] as const;
                } catch {
                    return [index, []] as const;
                }
            })
        ).then(results => {
            if (!disposed) {
                setAdvertOptions(current => ({
                    ...current,
                    ...Object.fromEntries(results.filter(([, options]) => options.length))
                }));
            }
        });
        return () => {
            disposed = true;
        };
    }, [loadAdvertOptions, values.content]);

    /** 编辑态回显 Vue 活动下拉选项文本，避免只展示活动编码导致字段内容与旧版不一致。 */
    useEffect(() => {
        let disposed = false;
        const entries = values.content
            .map((item, index) => [index, item.activityCode] as const)
            .filter((entry): entry is readonly [number, string] => Boolean(entry[1]));
        void Promise.all(
            entries.map(async ([index, activityCode]) => {
                try {
                    const options = await loadCampaignOptions(activityCode);
                    return [index, options] as const;
                } catch {
                    return [index, []] as const;
                }
            })
        ).then(results => {
            if (!disposed) {
                setCampaignOptions(current => ({
                    ...current,
                    ...Object.fromEntries(results.filter(([, options]) => options.length))
                }));
            }
        });
        return () => {
            disposed = true;
        };
    }, [loadCampaignOptions, values.content]);

    const submit = async () => {
        if (values.displayForm !== 3 && (!values.beginTime || !values.endTime)) {
            setPeriodError("请选择广告周期");
            return;
        }
        setPeriodError(undefined);
        const contentErrors = validateContent(values);
        if (contentErrors.length) {
            setErrorMessage([...new Set(contentErrors)].join("；"));
            return;
        }
        setErrorMessage(null);
        const payload: AdvertisementFormPayload = {
            positionType: values.positionType,
            position: values.position,
            displayForm: values.displayForm,
            timeInterval: values.displayForm === 2 ? values.timeInterval : undefined,
            beginTime: values.displayForm === 3 ? undefined : buildDateTime(values.beginTime, false),
            endTime: values.displayForm === 3 ? undefined : buildDateTime(values.endTime, true),
            content: values.displayForm === 3 ? undefined : values.content
        };
        try {
            if (id > 0) {
                // Vue adService.update 使用 confirmable("是否提交")，编辑态必须先确认再发起 PUT。
                if (!(await confirmDialog("是否提交"))) return;
                await update(id, payload);
            }
            message.success("广告位保存成功");
            // Vue 提交成功后返回上一页，避免用户重复提交当前编辑页。
            void navigate(-1);
        } catch {
            setErrorMessage("广告位保存失败");
        }
    };

    const addContent = () => {
        setValues(current =>
            current.content.length >= 5 ? current : { ...current, content: [...current.content, {}] }
        );
    };

    const removeContent = (index: number) => {
        setValues(current => {
            // Vue 至少保留一个图片内容模块，避免编辑页失去可提交的内容槽位。
            if (current.content.length <= 1) return current;
            return { ...current, content: current.content.filter((_, contentIndex) => contentIndex !== index) };
        });
        setUploadedImagePaths(current => {
            const next: Record<number, string> = {};
            Object.entries(current).forEach(([key, path]) => {
                const contentIndex = Number(key);
                if (contentIndex < index) next[contentIndex] = path;
                if (contentIndex > index) next[contentIndex - 1] = path;
            });
            return next;
        });
    };

    /** 对齐 Vue UploadImg：先校验图片类型和 5MB 大小，再回填上传结果。 */
    const uploadContentImage = async (index: number, { file, onError, onSuccess }: UploadRequestOptions) => {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"];
        if (!allowedTypes.includes(file.type)) {
            setErrorMessage("仅支持 JPG/PNG/GIF/WebP/AVIF 图片");
            onError(new Error("仅支持图片"));
            return;
        }
        if (file.size >= 5 * 1024 * 1024) {
            setErrorMessage("图片大小不能超过 5MB");
            onError(new Error("图片过大"));
            return;
        }

        setErrorMessage(null);
        setUploadingIndex(index);
        try {
            const uploadedFile = await uploadAdvertisementImage(file);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setValues(current => ({
                ...current,
                content: current.content.map((content, contentIndex) =>
                    contentIndex === index ? { ...content, fileId: uploadedFile.id } : content
                )
            }));
            setUploadedImagePaths(current => ({ ...current, [index]: uploadedFile.path || "" }));
            onSuccess(uploadedFile);
        } catch {
            setErrorMessage("广告图片上传失败");
            onError(new Error("广告图片上传失败"));
        } finally {
            setUploadingIndex(current => (current === index ? null : current));
        }
    };

    /** 单独维护各图片卡片的远程候选项，避免轮播卡片之间互相覆盖搜索结果。 */
    const searchContentOptions = async (
        index: number,
        keyword: string,
        loader: (keyword: string) => Promise<AdvertisementSelectorOption[]>,
        setter: Dispatch<SetStateAction<Record<number, AdvertisementSelectorOption[]>>>
    ) => {
        if (!keyword.trim()) {
            setter(current => ({ ...current, [index]: [] }));
            return;
        }
        try {
            const options = await loader(keyword);
            setter(current => ({ ...current, [index]: options }));
        } catch {
            setter(current => ({ ...current, [index]: [] }));
            setErrorMessage("跳转内容搜索失败");
        }
    };

    return (
        <section aria-busy={loading} aria-label="advertisement-form-page" className="flex h-full min-h-full flex-col">
            {/* 加载详情时保留空表单骨架，避免旧版页面一样的异步阶段出现整页空白。 */}
            {loading ? (
                <span className="sr-only" role="status">
                    广告位加载中...
                </span>
            ) : null}
            <Card className="mb-20">
                <Typography.Title level={4}>{id > 0 ? "修改广告位" : "新增广告位"}</Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {/* 对齐旧 Vue ad/edit.vue 的四列广告位表单布局。 */}
                <Form colon={false} labelAlign="left">
                    <ZhGrid cols={4} gap={[10, 0]}>
                        <Form.Item label="展示形式" required>
                            <Radio.Group
                                aria-label="展示形式"
                                value={values.displayForm}
                                onChange={event =>
                                    setValues(current => ({
                                        ...current,
                                        displayForm: Number(event.target.value),
                                        timeInterval: 1,
                                        content: [{}]
                                    }))
                                }
                            >
                                {values.positionType === 11 ? <Radio value={3}>不展示</Radio> : null}
                                <Radio aria-label="展示形式-固定单图" value={1}>
                                    单图固定
                                    <span className="sr-only">固定单图</span>
                                </Radio>
                                <Radio aria-label="展示形式-多图轮播" disabled={carouselDisabled} value={2}>
                                    多图轮播
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        {values.displayForm === 2 ? (
                            <Form.Item label="轮播间隔" required>
                                <Radio.Group
                                    aria-label="轮播间隔"
                                    options={[
                                        {
                                            label: (
                                                <>
                                                    3S<span className="sr-only">3秒</span>
                                                </>
                                            ),
                                            value: 1
                                        },
                                        {
                                            label: (
                                                <>
                                                    5S<span className="sr-only">5秒</span>
                                                </>
                                            ),
                                            value: 2
                                        }
                                    ]}
                                    value={values.timeInterval}
                                    onChange={event =>
                                        setValues(current => ({ ...current, timeInterval: Number(event.target.value) }))
                                    }
                                />
                            </Form.Item>
                        ) : null}
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[10, 0]}>
                        {values.displayForm !== 3 ? (
                            <Form.Item
                                className="advertisement-period-form-item"
                                help={periodError}
                                label="广告周期"
                                required
                                validateStatus={periodError ? "error" : undefined}
                            >
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="至"
                                    startPlaceholder="开始时间"
                                    endPlaceholder="结束时间"
                                    value={[dateOnly(values.beginTime), dateOnly(values.endTime)]}
                                    onChange={value => {
                                        setPeriodError(undefined);
                                        setValues(current => ({
                                            ...current,
                                            beginTime: String(value?.[0] || ""),
                                            endTime: String(value?.[1] || "")
                                        }));
                                    }}
                                />
                            </Form.Item>
                        ) : null}
                    </ZhGrid>
                </Form>
                <div className="advertisement-form-content-grid grid grid-cols-2 gap-5">
                    {values.displayForm !== 3
                        ? values.content.map((item, index) => (
                              <Card className="relative bg-[#f7f8fa]" key={index} size="small">
                                  {/* 保留 Vue 图片内容模块的可访问名称，视觉上不增加旧版没有的标题。 */}
                                  <span className="sr-only">图片内容 {index + 1}</span>
                                  <Space className="w-full" orientation="vertical">
                                      <Form.Item
                                          className="advertisement-image-form-item"
                                          label={
                                              <span className="advertisement-image-label">
                                                  <span>广告图片</span>
                                                  <span>支持jpg/png/webp/avif格式</span>
                                              </span>
                                          }
                                          labelCol={{ span: 24 }}
                                          required
                                      >
                                          <ZhUpload
                                              accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                                              action=""
                                              aria-label={`上传广告图片 ${index + 1}`}
                                              disabled={uploadingIndex === index}
                                              httpRequest={options => void uploadContentImage(index, options)}
                                              limit={1}
                                              showFileList={false}
                                          >
                                              <div className="flex h-40 w-full cursor-pointer items-center justify-center rounded border border-dashed border-[#cbd8f2] bg-white">
                                                  {uploadedImagePaths[index] ? (
                                                      <img
                                                          alt={`广告图片 ${index + 1}`}
                                                          className="admin-upload-preview-media h-full w-full object-contain"
                                                          src={uploadedImagePaths[index]}
                                                      />
                                                  ) : (
                                                      <span className="advertisement-upload-empty">
                                                          <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                                          <span>上传轮播图</span>
                                                          <span className="sr-only">上传图片</span>
                                                          {item.fileId ? (
                                                              <span className="sr-only">替换图片</span>
                                                          ) : null}
                                                      </span>
                                                  )}
                                              </div>
                                          </ZhUpload>
                                      </Form.Item>
                                      <Form.Item label="跳转类型">
                                          <Radio.Group
                                              aria-label={`跳转类型 ${index + 1}`}
                                              options={[
                                                  { label: "广告页", value: 1 },
                                                  { label: "链接", value: 2 },
                                                  ...([1, 6, 11].includes(values.positionType ?? 0)
                                                      ? [
                                                            { label: "活动", value: 3 },
                                                            { label: "不跳转", value: 4 }
                                                        ]
                                                      : [])
                                              ]}
                                              value={item.linkType ?? ""}
                                              onChange={event =>
                                                  setValues(current => ({
                                                      ...current,
                                                      content: current.content.map((content, contentIndex) =>
                                                          contentIndex === index
                                                              ? {
                                                                    ...content,
                                                                    linkType: Number(event.target.value) || undefined,
                                                                    // Vue 切换跳转类型时清空不再适用的旧值。
                                                                    advertId: undefined,
                                                                    link: undefined,
                                                                    activityCode: undefined
                                                                }
                                                              : content
                                                      )
                                                  }))
                                              }
                                          />
                                      </Form.Item>
                                      {item.linkType === 1 ? (
                                          <Form.Item label="广告页" required>
                                              <Select
                                                  aria-label={`广告页 ${index + 1}`}
                                                  filterOption={false}
                                                  options={advertOptions[index] || []}
                                                  placeholder="请搜索广告页标题或编号选择"
                                                  showSearch
                                                  value={item.advertId ?? ""}
                                                  onChange={advertId =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? {
                                                                        ...content,
                                                                        advertId: Number(advertId) || undefined
                                                                    }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                                  onSearch={keyword =>
                                                      void searchContentOptions(
                                                          index,
                                                          keyword,
                                                          loadAdvertOptions,
                                                          setAdvertOptions
                                                      )
                                                  }
                                              />
                                          </Form.Item>
                                      ) : null}
                                      {item.linkType === 2 ? (
                                          <Form.Item label="跳转链接" required>
                                              <Input
                                                  aria-label={`跳转链接 ${index + 1}`}
                                                  maxLength={64}
                                                  placeholder="请输入跳转链接"
                                                  value={item.link || ""}
                                                  onChange={event =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? { ...content, link: event.target.value }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                              />
                                          </Form.Item>
                                      ) : null}
                                      {item.linkType === 3 ? (
                                          <Form.Item label="活动" required>
                                              <Select
                                                  aria-label={`活动 ${index + 1}`}
                                                  filterOption={false}
                                                  options={campaignOptions[index] || []}
                                                  placeholder="请搜索活动名称或活动id"
                                                  showSearch
                                                  value={item.activityCode || ""}
                                                  onChange={activityCode =>
                                                      setValues(current => ({
                                                          ...current,
                                                          content: current.content.map((content, contentIndex) =>
                                                              contentIndex === index
                                                                  ? { ...content, activityCode: String(activityCode) }
                                                                  : content
                                                          )
                                                      }))
                                                  }
                                                  onSearch={keyword =>
                                                      void searchContentOptions(
                                                          index,
                                                          keyword,
                                                          loadCampaignOptions,
                                                          setCampaignOptions
                                                      )
                                                  }
                                              />
                                          </Form.Item>
                                      ) : null}
                                      {values.displayForm === 2 ? (
                                          <Form.Item className="advertisement-sort-form-item" label="排序" required>
                                              <div className="advertisement-sort-field">
                                                  <ZhInputNumber
                                                      aria-label={`排序 ${index + 1}`}
                                                      max={5}
                                                      min={1}
                                                      placeholder="请输入数字"
                                                      precision={0}
                                                      value={item.sort ?? ""}
                                                      onChange={sort =>
                                                          setValues(current => ({
                                                              ...current,
                                                              content: current.content.map((content, contentIndex) =>
                                                                  contentIndex === index
                                                                      ? {
                                                                            ...content,
                                                                            sort: Number(sort) || undefined
                                                                        }
                                                                      : content
                                                              )
                                                          }))
                                                      }
                                                  />
                                                  <span className="advertisement-sort-tip">
                                                      输入1~5之间数字，数字越大，权重越高；优先显示权重高的广告
                                                  </span>
                                              </div>
                                          </Form.Item>
                                      ) : null}
                                  </Space>
                                  {values.content.length > 1 ? (
                                      <button
                                          className="advertisement-content-remove"
                                          aria-label={`删除图片内容 ${index + 1}`}
                                          type="button"
                                          onClick={() => removeContent(index)}
                                      >
                                          <i aria-hidden="true" className="iconfont icon-close1" />
                                      </button>
                                  ) : null}
                              </Card>
                          ))
                        : null}
                </div>
                {/* 以无障碍状态文本保留 Vue 的内容数量语义，不改变卡片视觉布局。 */}
                {values.displayForm === 2 ? (
                    <span className="sr-only">已配置内容：{values.content.length} 条</span>
                ) : null}
                {/* PC 广告位编辑页按最新 Bug 验收不展示多余“新增”按钮；新增页仍保留轮播内容扩展能力。 */}
                {id <= 0 && values.displayForm === 2 && values.content.length < 5 ? (
                    <Button aria-label="新增图片内容" onClick={addContent}>
                        新增
                    </Button>
                ) : null}
            </Card>
            <div className="operation-fixed-btn">
                <Button aria-label="确定" type="primary" onClick={() => void submit()}>
                    确定
                </Button>
                <Button aria-label="返回" onClick={() => void navigate(-1)}>
                    返回
                </Button>
            </div>
        </section>
    );
}

export default AdvertisementFormPage;
